// src/utils/excelProcessing.js
// All Excel analysis utilities — SheetJS for parsing, pure JS for analysis
// Completely independent from csv.js — separate pipeline

// ─────────────────────────────────────────────
// SHEETJS LOADER (from CDN, cached on window)
// ─────────────────────────────────────────────

export const loadSheetJS = () => {
  return new Promise((resolve, reject) => {
    if (window.XLSX) { resolve(window.XLSX); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    script.onload = () => resolve(window.XLSX);
    script.onerror = () => reject(new Error('Failed to load SheetJS from CDN'));
    document.head.appendChild(script);
  });
};

// ─────────────────────────────────────────────
// EXCEL PARSING
// ─────────────────────────────────────────────

export const parseExcel = async (file) => {
  const XLSX = await loadSheetJS();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array', cellDates: true, cellNF: true, cellStyles: true });

        const sheets = {};
        wb.SheetNames.forEach(name => {
          const ws = wb.Sheets[name];
          const ref = ws['!ref'];
          if (!ref) { sheets[name] = { headers: [], rows: [], shape: [0,0], empty: true }; return; }

          const range = XLSX.utils.decode_range(ref);
          const merges = (ws['!merges'] || []).map(m => ({
            from: XLSX.utils.encode_cell(m.s),
            to: XLSX.utils.encode_cell(m.e),
            rows: m.e.r - m.s.r + 1,
            cols: m.e.c - m.s.c + 1,
          }));

          // Get raw 2D array (preserve types)
          const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
          if (raw.length < 1) { sheets[name] = { headers: [], rows: [], shape: [0,0], empty: true }; return; }

          // Find first non-empty row as header
          let hIdx = 0;
          for (let i = 0; i < Math.min(5, raw.length); i++) {
            if (raw[i] && raw[i].some(c => c !== null && c !== '')) { hIdx = i; break; }
          }

          const headers = (raw[hIdx] || []).map((h, i) =>
            h !== null && h !== '' ? String(h).trim() : `Col_${i + 1}`
          );

          const dataRows = raw.slice(hIdx + 1).filter(r => r && r.some(c => c !== null && c !== ''));
          const rows = dataRows.map(row => {
            const obj = {};
            headers.forEach((h, i) => {
              const v = row[i];
              // Preserve numbers as numbers, dates as strings, nulls as ''
              if (v === null || v === undefined) obj[h] = '';
              else if (v instanceof Date) obj[h] = v.toISOString().slice(0, 10);
              else obj[h] = String(v).trim();
            });
            return obj;
          });

          // Extract cell formulas and styles (sample)
          const formulaCells = [];
          const styleCells = [];
          for (const addr in ws) {
            if (addr.startsWith('!')) continue;
            const cell = ws[addr];
            if (cell.f) formulaCells.push({ addr, formula: '=' + cell.f });
            if (cell.s) styleCells.push({ addr, style: cell.s });
          }

          // Detect named ranges
          const namedRanges = wb.Workbook?.Names?.map(n => ({
            name: n.Name,
            ref: n.Ref,
            sheet: n.Sheet,
          })) || [];

          sheets[name] = {
            headers,
            rows,
            shape: [rows.length, headers.length],
            dimensions: {
              rows: range.e.r - range.s.r + 1,
              cols: range.e.c - range.s.c + 1,
            },
            merges,
            formulaCount: formulaCells.length,
            formulas: formulaCells.slice(0, 50),
            namedRanges: namedRanges.slice(0, 20),
          };
        });

        resolve({
          sheets,
          sheetNames: wb.SheetNames,
          activeSheet: wb.SheetNames[0],
          fileName: file.name,
          fileSize: file.size,
          fileType: file.name.split('.').pop().toLowerCase(),
          totalSheets: wb.SheetNames.length,
          namedRanges: wb.Workbook?.Names || [],
        });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// ─────────────────────────────────────────────
// TYPE INFERENCE (numeric, date, categorical, boolean, mixed)
// ─────────────────────────────────────────────

export const inferExcelTypes = (headers, rows) => {
  return headers.map(col => {
    const vals = rows.map(r => r[col]).filter(v => v !== '' && v !== null && v !== undefined);
    if (vals.length === 0) return { col, type: 'empty', nullCount: rows.length, uniqueCount: 0 };

    const nullCount = rows.length - vals.length;

    // Date detection
    const dateRe = /^\d{4}-\d{2}-\d{2}|^\d{2}\/\d{2}\/\d{4}|^\d{2}-\d{2}-\d{4}/;
    const dateRatio = vals.filter(v => dateRe.test(String(v))).length / vals.length;
    if (dateRatio > 0.7) return { col, type: 'date', nullCount, uniqueCount: new Set(vals).size };

    // Boolean detection
    const boolVals = new Set(['true','false','yes','no','1','0','y','n']);
    const boolRatio = vals.filter(v => boolVals.has(String(v).toLowerCase())).length / vals.length;
    if (boolRatio > 0.8) return { col, type: 'boolean', nullCount, uniqueCount: new Set(vals).size };

    // Numeric detection
    const numVals = vals.filter(v => {
      const s = String(v).replace(/[$,%]/g, '').replace(/,/g, '');
      return !isNaN(parseFloat(s)) && isFinite(parseFloat(s));
    });
    const numRatio = numVals.length / vals.length;

    if (numRatio > 0.8) {
      // Sub-classify numeric
      const nums = numVals.map(v => parseFloat(String(v).replace(/[$,%,]/g, '').replace(/,/g, '')));
      const allInts = nums.every(n => Number.isInteger(n));
      const hasPercent = vals.some(v => String(v).includes('%'));
      const hasCurrency = vals.some(v => /^[$€£¥]/.test(String(v)));
      const subType = hasPercent ? 'percentage' : hasCurrency ? 'currency' : allInts ? 'integer' : 'float';
      return { col, type: 'numeric', subType, nullCount, uniqueCount: new Set(vals).size };
    }

    return { col, type: 'categorical', nullCount, uniqueCount: new Set(vals).size };
  });
};

// Parse numeric value from various formats
export const parseNum = (v) => {
  if (v === '' || v === null || v === undefined) return NaN;
  const s = String(v).replace(/[$€£¥,%]/g, '').replace(/,/g, '').trim();
  return parseFloat(s);
};

// ─────────────────────────────────────────────
// STATISTICS (full suite)
// ─────────────────────────────────────────────

export const calcStats = (rows, col) => {
  const vals = rows.map(r => parseNum(r[col])).filter(v => !isNaN(v) && isFinite(v)).sort((a,b)=>a-b);
  if (vals.length === 0) return null;
  const n = vals.length;
  const mean = vals.reduce((s,v) => s+v, 0) / n;
  const variance = vals.reduce((s,v) => s+(v-mean)**2, 0) / n;
  const std = Math.sqrt(variance);
  const q1 = vals[Math.floor(n*0.25)];
  const median = n%2===0 ? (vals[n/2-1]+vals[n/2])/2 : vals[Math.floor(n/2)];
  const q3 = vals[Math.floor(n*0.75)];
  const iqr = q3 - q1;
  const skewness = std > 0 ? (vals.reduce((s,v) => s+(v-mean)**3, 0)/n) / std**3 : 0;
  const kurtosis = std > 0 ? (vals.reduce((s,v) => s+(v-mean)**4, 0)/n) / std**4 - 3 : 0;
  const cv = mean !== 0 ? (std / Math.abs(mean)) * 100 : 0;
  const mad = vals.reduce((s,v) => s+Math.abs(v-mean), 0) / n;
  const lf = q1 - 1.5*iqr, uf = q3 + 1.5*iqr;
  const outliers = vals.filter(v => v < lf || v > uf);
  const pct = p => vals[Math.min(n-1, Math.floor(p/100*n))];
  const sum = vals.reduce((s,v) => s+v, 0);

  return {
    n, sum: +sum.toFixed(4), mean: +mean.toFixed(4), median: +median.toFixed(4),
    std: +std.toFixed(4), variance: +variance.toFixed(4), cv: +cv.toFixed(2),
    mad: +mad.toFixed(4), min: vals[0], max: vals[n-1],
    range: +(vals[n-1]-vals[0]).toFixed(4),
    q1: +q1.toFixed(4), q3: +q3.toFixed(4), iqr: +iqr.toFixed(4),
    skewness: +skewness.toFixed(4), kurtosis: +kurtosis.toFixed(4),
    p5: +pct(5).toFixed(4), p10: +pct(10).toFixed(4),
    p25: +q1.toFixed(4), p75: +q3.toFixed(4),
    p90: +pct(90).toFixed(4), p95: +pct(95).toFixed(4), p99: +pct(99).toFixed(4),
    outlierCount: outliers.length, outlierPct: +(outliers.length/n*100).toFixed(1),
    lf: +lf.toFixed(4), uf: +uf.toFixed(4),
  };
};

// ─────────────────────────────────────────────
// HISTOGRAM
// ─────────────────────────────────────────────

export const calcHistogram = (rows, col, bins = 20) => {
  const vals = rows.map(r => parseNum(r[col])).filter(v => !isNaN(v) && isFinite(v));
  if (vals.length === 0) return [];
  const min = Math.min(...vals), max = Math.max(...vals);
  if (min === max) return [{ range: String(min), count: vals.length, min, max }];
  const bw = (max - min) / bins;
  const buckets = Array.from({length: bins}, (_, i) => ({
    range: `${(min+i*bw).toFixed(2)}–${(min+(i+1)*bw).toFixed(2)}`,
    count: 0, min: min+i*bw, max: min+(i+1)*bw,
  }));
  vals.forEach(v => {
    const bi = Math.min(bins-1, Math.floor((v-min)/bw));
    buckets[bi].count++;
  });
  return buckets;
};

// ─────────────────────────────────────────────
// VALUE COUNTS
// ─────────────────────────────────────────────

export const calcValueCounts = (rows, col, topN = 20) => {
  const freq = {};
  rows.forEach(r => {
    const v = r[col] || '(blank)';
    freq[v] = (freq[v] || 0) + 1;
  });
  const total = rows.length;
  return Object.entries(freq)
    .sort((a,b) => b[1]-a[1])
    .slice(0, topN)
    .map(([value, count]) => ({ value, count, pct: +(count/total*100).toFixed(1) }));
};

// ─────────────────────────────────────────────
// PEARSON CORRELATION
// ─────────────────────────────────────────────

export const calcCorrelation = (rows, c1, c2) => {
  const pairs = rows
    .map(r => [parseNum(r[c1]), parseNum(r[c2])])
    .filter(([a,b]) => !isNaN(a) && !isNaN(b) && isFinite(a) && isFinite(b));
  const n = pairs.length;
  if (n < 3) return 0;
  const m1 = pairs.reduce((s,[a]) => s+a, 0)/n;
  const m2 = pairs.reduce((s,[,b]) => s+b, 0)/n;
  let num=0, d1=0, d2=0;
  pairs.forEach(([a,b]) => { num+=(a-m1)*(b-m2); d1+=(a-m1)**2; d2+=(b-m2)**2; });
  return d1&&d2 ? +(num/Math.sqrt(d1*d2)).toFixed(3) : 0;
};

export const calcCorrelationMatrix = (rows, numCols) => {
  const cols = numCols.slice(0, 12);
  const matrix = cols.map((c1, i) =>
    cols.map((c2, j) => i === j ? 1 : calcCorrelation(rows, c1, c2))
  );
  return { matrix, cols };
};

// ─────────────────────────────────────────────
// PIVOT TABLE
// ─────────────────────────────────────────────

export const buildPivot = (rows, rowKey, colKey, valKey, aggFn = 'sum') => {
  const rowVals = [...new Set(rows.map(r => r[rowKey]).filter(Boolean))].sort().slice(0, 30);
  const colVals = [...new Set(rows.map(r => r[colKey]).filter(Boolean))].sort().slice(0, 15);

  const buckets = {};
  rows.forEach(r => {
    const rk = r[rowKey], ck = r[colKey], v = parseNum(r[valKey]);
    if (!rk || !ck || isNaN(v)) return;
    const k = `${rk}||${ck}`;
    if (!buckets[k]) buckets[k] = [];
    buckets[k].push(v);
  });

  const agg = (arr, fn) => {
    if (!arr || !arr.length) return null;
    if (fn === 'sum')    return arr.reduce((s,v)=>s+v,0);
    if (fn === 'mean')   return arr.reduce((s,v)=>s+v,0)/arr.length;
    if (fn === 'count')  return arr.length;
    if (fn === 'min')    return Math.min(...arr);
    if (fn === 'max')    return Math.max(...arr);
    if (fn === 'median') { const s=[...arr].sort((a,b)=>a-b); return s[Math.floor(s.length/2)]; }
    if (fn === 'std')    { const m=arr.reduce((s,v)=>s+v,0)/arr.length; return Math.sqrt(arr.reduce((s,v)=>s+(v-m)**2,0)/arr.length); }
    return arr.reduce((s,v)=>s+v,0);
  };

  const data = rowVals.map(rv => {
    const obj = { __row: rv, __total: 0 };
    let rowTot = 0;
    colVals.forEach(cv => {
      const v = agg(buckets[`${rv}||${cv}`], aggFn);
      obj[cv] = v !== null ? +v.toFixed(2) : null;
      if (v !== null) rowTot += v;
    });
    obj.__total = +rowTot.toFixed(2);
    return obj;
  });

  const totals = { __row: 'TOTAL' };
  colVals.forEach(cv => {
    const all = rowVals.flatMap(rv => buckets[`${rv}||${cv}`] || []);
    totals[cv] = all.length ? +agg(all, aggFn).toFixed(2) : null;
  });
  totals.__total = +(data.reduce((s,r) => s+(r.__total||0), 0)).toFixed(2);

  return { data, totals, rowVals, colVals, rowKey, colKey, valKey, aggFn };
};

// ─────────────────────────────────────────────
// GROUP-BY AGGREGATION
// ─────────────────────────────────────────────

export const groupBy = (rows, groupCol, valCol, aggFn = 'sum') => {
  const groups = {};
  rows.forEach(r => {
    const gk = r[groupCol] || '(blank)';
    const v = parseNum(r[valCol]);
    if (!groups[gk]) groups[gk] = { vals: [], count: 0 };
    groups[gk].count++;
    if (!isNaN(v)) groups[gk].vals.push(v);
  });

  const agg = (arr, fn) => {
    if (!arr.length) return 0;
    if (fn === 'sum')  return arr.reduce((s,v)=>s+v,0);
    if (fn === 'mean') return arr.reduce((s,v)=>s+v,0)/arr.length;
    if (fn === 'count') return arr.length;
    if (fn === 'min')  return Math.min(...arr);
    if (fn === 'max')  return Math.max(...arr);
    if (fn === 'std')  { const m=arr.reduce((s,v)=>s+v,0)/arr.length; return Math.sqrt(arr.reduce((s,v)=>s+(v-m)**2,0)/arr.length); }
    return arr.reduce((s,v)=>s+v,0);
  };

  return Object.entries(groups)
    .map(([group, {vals, count}]) => ({
      group,
      value: +agg(vals, aggFn).toFixed(3),
      count,
      sum: vals.length ? +vals.reduce((s,v)=>s+v,0).toFixed(3) : 0,
      mean: vals.length ? +(vals.reduce((s,v)=>s+v,0)/vals.length).toFixed(3) : 0,
      min: vals.length ? +Math.min(...vals).toFixed(3) : 0,
      max: vals.length ? +Math.max(...vals).toFixed(3) : 0,
    }))
    .sort((a,b) => b.value - a.value);
};

// ─────────────────────────────────────────────
// TIME SERIES DETECTION + ANALYSIS
// ─────────────────────────────────────────────

export const detectDateCols = (headers, rows) => {
  const patterns = [
    /^\d{4}-\d{2}-\d{2}/,
    /^\d{2}\/\d{2}\/\d{4}/,
    /^\d{4}$/,
    /^Q[1-4]\s*\d{4}/i,
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i,
  ];
  return headers.filter(h => {
    const sample = rows.slice(0, 20).map(r => String(r[h] || ''));
    return sample.filter(v => patterns.some(p => p.test(v.trim()))).length >= Math.min(3, sample.length * 0.5);
  });
};

const toTimestamp = (str) => {
  str = String(str).trim();
  const d = new Date(str);
  if (!isNaN(d.getTime())) return d.getTime();
  const qm = str.match(/Q([1-4])\s*(\d{4})/i);
  if (qm) return new Date(parseInt(qm[2]), (parseInt(qm[1])-1)*3, 1).getTime();
  return null;
};

export const buildTimeSeries = (rows, dateCol, valCol) => {
  return rows
    .map(r => ({ ts: toTimestamp(r[dateCol]), val: parseNum(r[valCol]), label: r[dateCol] }))
    .filter(d => d.ts && !isNaN(d.val) && isFinite(d.val))
    .sort((a,b) => a.ts - b.ts)
    .map(d => ({ date: d.label, value: d.val, timestamp: d.ts }));
};

export const movingAverage = (series, w = 3) =>
  series.map((d, i) => {
    const s = Math.max(0, i-Math.floor(w/2)), e = Math.min(series.length, s+w);
    const avg = series.slice(s,e).reduce((a,x)=>a+x.value,0)/(e-s);
    return { ...d, ma: +avg.toFixed(3) };
  });

export const linearTrend = (series) => {
  const n = series.length;
  if (n < 2) return series.map(d=>({...d, trend: d.value}));
  const mx = (n-1)/2, my = series.reduce((s,d)=>s+d.value,0)/n;
  let num=0, den=0;
  series.forEach((d,i) => { num+=(i-mx)*(d.value-my); den+=(i-mx)**2; });
  const slope = den ? num/den : 0;
  const intercept = my - slope*mx;
  return series.map((d,i)=>({...d, trend: +(slope*i+intercept).toFixed(3), slope: +slope.toFixed(4)}));
};

// YoY / MoM growth rates
export const calcGrowthRates = (series) =>
  series.map((d, i) => ({
    ...d,
    growth: i > 0 && series[i-1].value !== 0
      ? +((d.value - series[i-1].value) / Math.abs(series[i-1].value) * 100).toFixed(2)
      : null,
  }));

// ─────────────────────────────────────────────
// ANOMALY DETECTION (Z-score + IQR combo)
// ─────────────────────────────────────────────

export const detectAnomalies = (rows, numCols, zThreshold = 3) => {
  const colStats = {};
  numCols.forEach(col => {
    const vals = rows.map(r => parseNum(r[col])).filter(v => !isNaN(v) && isFinite(v));
    if (!vals.length) return;
    const mean = vals.reduce((s,v)=>s+v,0)/vals.length;
    const std = Math.sqrt(vals.reduce((s,v)=>s+(v-mean)**2,0)/vals.length);
    const sorted = [...vals].sort((a,b)=>a-b);
    const q1 = sorted[Math.floor(sorted.length*0.25)];
    const q3 = sorted[Math.floor(sorted.length*0.75)];
    const iqr = q3-q1;
    colStats[col] = { mean, std, q1, q3, iqr, lf: q1-1.5*iqr, uf: q3+1.5*iqr };
  });

  const anomalies = [];
  rows.forEach((row, idx) => {
    const hits = {};
    numCols.forEach(col => {
      const v = parseNum(row[col]);
      if (isNaN(v) || !colStats[col]) return;
      const { mean, std, lf, uf } = colStats[col];
      const z = std > 0 ? Math.abs((v-mean)/std) : 0;
      if (z > zThreshold || v < lf || v > uf) hits[col] = { value: v, z: +z.toFixed(2), iqrFlag: v<lf||v>uf };
    });
    if (Object.keys(hits).length) anomalies.push({ rowIdx: idx, hits, row });
  });

  return { anomalies: anomalies.slice(0, 200), colStats, totalAnomalyRows: anomalies.length };
};

// ─────────────────────────────────────────────
// DATA PROFILING (per-column quality report)
// ─────────────────────────────────────────────

export const profileColumns = (rows, colTypes) =>
  colTypes.map(ct => {
    const vals = rows.map(r => r[ct.col]).filter(v => v !== '' && v !== null);
    const nullCount = rows.length - vals.length;
    const nullPct = +(nullCount/rows.length*100).toFixed(1);

    let quality = 100;
    const issues = [];
    if (nullPct > 50) { quality -= 40; issues.push(`${nullPct}% missing`); }
    else if (nullPct > 10) { quality -= 15; issues.push(`${nullPct}% missing`); }
    else if (nullPct > 0)  { quality -= 5;  issues.push(`${nullPct}% missing`); }

    if (ct.type === 'numeric') {
      const stats = calcStats(rows, ct.col);
      if (stats) {
        if (stats.outlierPct > 10) { quality -= 20; issues.push(`${stats.outlierPct}% outliers`); }
        if (Math.abs(stats.skewness) > 3) { quality -= 10; issues.push(`high skewness`); }
        if (stats.std === 0) { quality -= 30; issues.push('zero variance'); }
      }
      const score = Math.max(0, quality);
      return { ...ct, nullCount, nullPct, stats, quality: { score, label: score >= 80 ? 'Good' : score >= 60 ? 'Fair' : 'Poor' }, issues };
    }

    const uniqR = ct.uniqueCount / rows.length;
    if (uniqR > 0.95 && rows.length > 50) { quality -= 15; issues.push('likely ID col'); }
    const topVals = calcValueCounts(rows, ct.col, 5);
    const score = Math.max(0, quality);
    return { ...ct, nullCount, nullPct, topVals, quality: { score, label: score >= 80 ? 'Good' : score >= 60 ? 'Fair' : 'Poor' }, issues };
  });

// ─────────────────────────────────────────────
// MISSING VALUE PATTERN HEATMAP
// ─────────────────────────────────────────────

export const getMissingMatrix = (rows, headers, sampleSize = 80) => {
  const sample = rows.slice(0, sampleSize);
  return sample.map((row, ri) => {
    const obj = { row: ri };
    headers.forEach(h => { obj[h] = row[h] === '' || row[h] === null || row[h] === undefined ? 1 : 0; });
    return obj;
  });
};

// ─────────────────────────────────────────────
// CROSS-SHEET ANALYSIS
// ─────────────────────────────────────────────

export const crossSheetSummary = (excelData) => {
  return excelData.sheetNames.map(name => {
    const sheet = excelData.sheets[name];
    if (sheet.empty) return { name, empty: true };
    const rows = sheet.rows, headers = sheet.headers;
    const types = inferExcelTypes(headers, rows);
    const numCols = types.filter(t => t.type === 'numeric' || t.type === 'integer' || t.type === 'float');
    const nullTotal = types.reduce((s, t) => s + t.nullCount, 0);
    const dupCount = rows.length - new Set(rows.map(r => JSON.stringify(r))).size;

    return {
      name,
      rows: rows.length,
      cols: headers.length,
      numericCols: numCols.length,
      nullTotal,
      nullPct: +(nullTotal / (rows.length * headers.length) * 100).toFixed(1),
      dupCount,
      formulaCount: sheet.formulaCount || 0,
      mergeCount: sheet.merges?.length || 0,
      headers: headers.slice(0, 8),
    };
  });
};

// ─────────────────────────────────────────────
// SAMPLE EXCEL WORKBOOK GENERATOR
// ─────────────────────────────────────────────

export const generateSampleExcel = async (type) => {
  const XLSX = await loadSheetJS();

  const wb = XLSX.utils.book_new();

  if (type === 'sales') {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const regions = ['North','South','East','West'];
    const products = ['Widget A','Widget B','Widget C','Service X','Service Y'];
    const rows = [['Month','Region','Product','Revenue','Units','Cost','Profit','Margin%']];
    months.forEach(m => regions.forEach(r => products.forEach(p => {
      const rev = +(Math.random()*50000+5000).toFixed(2);
      const cost = +(rev * (0.4 + Math.random()*0.3)).toFixed(2);
      const units = Math.floor(Math.random()*200+20);
      rows.push([m, r, p, rev, units, cost, +(rev-cost).toFixed(2), +((rev-cost)/rev*100).toFixed(1)]);
    })));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Sales Data');

    // Summary sheet
    const summary = [['Region','Total Revenue','Total Units','Avg Margin%'],
      ...regions.map(r => {
        const rrows = rows.slice(1).filter(row => row[1]===r);
        return [r, +rrows.reduce((s,row)=>s+row[3],0).toFixed(2), rrows.reduce((s,row)=>s+row[4],0), +rrows.reduce((s,row)=>s+row[7],0)/rrows.length.toFixed(1)];
      })];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
  }

  if (type === 'finance') {
    const years = [2021,2022,2023,2024];
    const rows = [['Metric','2021','2022','2023','2024']];
    const metrics = [
      ['Revenue', 100,120,145,168],['COGS',-60,-72,-87,-101],['Gross Profit',40,48,58,67],
      ['Operating Expenses',-20,-24,-28,-32],['EBITDA',20,24,30,35],
      ['Net Income',14,17,21,25],['EPS',1.4,1.7,2.1,2.5],
      ['Revenue Growth%',null,20,20.8,15.9],['EBITDA Margin%',20,20,20.7,20.8],
    ];
    metrics.forEach(m => rows.push(m));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Income Statement');

    const bs = [['Asset/Liability','2022','2023','2024'],
      ['Cash',30,45,62],['Accounts Receivable',18,22,26],['Inventory',12,14,16],
      ['Total Current Assets',60,81,104],['PP&E',80,90,100],['Total Assets',140,171,204],
      ['Accounts Payable',15,18,21],['LT Debt',50,45,40],['Total Equity',75,108,143]];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(bs), 'Balance Sheet');
  }

  if (type === 'hr') {
    const depts = ['Engineering','Marketing','Sales','Operations','HR','Finance'];
    const roles = ['Analyst','Senior Analyst','Manager','Director','VP'];
    const rows = [['EmpID','Department','Role','Salary','YearsExp','Rating','Gender','Location','Tenure']];
    for (let i = 0; i < 300; i++) {
      const dept = depts[Math.floor(Math.random()*depts.length)];
      const role = roles[Math.floor(Math.random()*roles.length)];
      const exp = Math.floor(Math.random()*20+1);
      const salary = +(50000 + exp*3000 + Math.random()*20000).toFixed(0);
      rows.push([`E${1000+i}`, dept, role, salary, exp, +(Math.random()*2+3).toFixed(1),
        Math.random()>0.48?'M':'F', ['NYC','SF','CHI','BOS','AUS'][Math.floor(Math.random()*5)],
        Math.floor(Math.random()*10+1)]);
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Employees');
  }

  if (type === 'survey') {
    const questions = ['Q1_Satisfaction','Q2_Ease','Q3_Support','Q4_Value','Q5_Recommend'];
    const segments = ['Enterprise','SMB','Startup','Individual'];
    const rows = [['ResponseID','Date','Segment','Country',...questions,'NPS','Comments']];
    const comments = ['Great product','Needs improvement','Love the UI','Slow support','Best in class','Too expensive','Easy to use'];
    for (let i = 0; i < 200; i++) {
      const scores = questions.map(() => Math.floor(Math.random()*5+1));
      const date = new Date(2024, Math.floor(Math.random()*12), Math.floor(Math.random()*28+1)).toISOString().slice(0,10);
      rows.push([`R${1000+i}`, date, segments[Math.floor(Math.random()*4)],
        ['USA','UK','Germany','India','Canada'][Math.floor(Math.random()*5)],
        ...scores, Math.floor(Math.random()*10+1), comments[Math.floor(Math.random()*comments.length)]]);
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Survey Responses');
  }

  const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbOut], { type: 'application/octet-stream' });
  return new File([blob], `sample_${type}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};